import { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  Alert,
} from '@mui/material';
import { toast } from 'react-toastify';
import { updateStakeDbPoolState } from '@/api/stake-db';
import { getAvailableStateTransitions, stakeDbStateFilter } from '@/lib/filter/stakeDbPoolState';

interface StakeDbPoolStateSelectProps {
  poolId: string;
  currentState: number;
  onStateChanged?: () => void;
}

export default function StakeDbPoolStateSelect({
  poolId,
  currentState,
  onStateChanged,
}: StakeDbPoolStateSelectProps) {
  const [selectedState, setSelectedState] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const availableTransitions = getAvailableStateTransitions(currentState);

  const handleStateChange = (event: SelectChangeEvent) => {
    setSelectedState(event.target.value);
  };

  const handleSubmit = async () => {
    if (!selectedState) {
      toast.error('Please select a state.');
      return;
    }

    setLoading(true);
    try {
      const re = await updateStakeDbPoolState({
        poolId,
        state: Number(selectedState),
      });

      if (re.status === 200) {
        toast.success(re.data.message || 'State updated successfully.');
        setSelectedState('');
        onStateChanged?.();
      } else {
        toast.error(re.data?.message || 'Failed to update state.');
      }
    } catch (error) {
      console.error('State update error:', error);
      toast.error('Failed to update state.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Change Pool State
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Current State: <strong>{stakeDbStateFilter(currentState)}</strong>
      </Typography>

      {availableTransitions.length === 0 ? (
        <Alert severity="info">
          No state transitions available from current state.
        </Alert>
      ) : (
        <>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>New State</InputLabel>
            <Select
              value={selectedState}
              label="New State"
              onChange={handleStateChange}
            >
              {availableTransitions.map((option) => (
                <MenuItem key={option.value} value={String(option.value)}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            fullWidth
            variant="contained"
            color="warning"
            onClick={handleSubmit}
            disabled={loading || !selectedState}
          >
            {loading ? 'Processing...' : 'Change State'}
          </Button>
        </>
      )}
    </Box>
  );
}
